import {
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  useComboBox,
  useFilter,
  Overlay,
  DismissButton,
  usePopover,
  type AriaComboBoxProps,
  type AriaPopoverProps,
} from 'react-aria';
import { useComboBoxState, type OverlayTriggerState } from 'react-stately';
import { useMeasure } from 'react-use';
import { InputDetail, SelectListBox } from '@hungryroot/ramen';
import styles from './AddressDropdown.module.css';

interface Props<T extends object> extends AriaComboBoxProps<T> {
  testid?: string;
  errorMessage?: string;
  className?: string;
  open?: boolean;
}

interface PopoverProps extends AriaPopoverProps {
  testid?: string;
  children: ReactNode;
  state: OverlayTriggerState;
  style: object;
  className?: string;
}

export function AddressDropdown<T extends object>(props: Props<T>) {
  const { testid, label, errorMessage, className, open, placeholder } = props;
  const { startsWith } = useFilter({ sensitivity: 'base' });
  const state = useComboBoxState({ ...props, defaultFilter: startsWith });

  const inputRef = useRef<HTMLInputElement>(null);
  const listBoxRef = useRef<HTMLUListElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const { listBoxProps, inputProps, labelProps } = useComboBox(
    {
      ...props,
      inputRef,
      listBoxRef,
      popoverRef,
    },
    state
  );

  useLayoutEffect(() => {
    inputRef.current?.setCustomValidity(errorMessage || '');
  }, []);

  useEffect(() => {
    if (open) {
      state.setOpen(true);
    }
  }, [open]);

  const [containerRef, { width: containerWidth }] =
    useMeasure<HTMLDivElement>();

  return (
    <div className={className}>
      <div
        ref={containerRef}
        data-testid={testid}
        className={`${styles.container} ${
          errorMessage ? styles['is-error'] : undefined
        }`}
      >
        <div className={styles['input-group']}>
          <input
            {...inputProps}
            // Note that chrome ignores autoComplete="off". This is a documented bug: https://bugs.chromium.org/p/chromium/issues/detail?id=587466
            autoComplete="off"
            data-testid={`${testid}-input`}
            placeholder={state.isFocused ? placeholder : undefined}
            ref={inputRef}
            className={`${styles.input} ${
              state.selectedItem ? styles['is-active'] : undefined
            }`}
          />
          <label {...labelProps} className={styles.label}>
            {label}
          </label>
        </div>
        {state.isOpen && (
          <AddressDropdown.Popover
            testid={`${testid}-popover`}
            state={state}
            triggerRef={inputRef}
            popoverRef={popoverRef}
            isNonModal
            placement="bottom start"
            style={{
              width: `${containerWidth}px`,
            }}
            className={
              errorMessage ? styles['popover-with-details'] : undefined
            }
          >
            <SelectListBox
              {...listBoxProps}
              state={state}
              testid={testid}
              listBoxRef={listBoxRef}
            />
          </AddressDropdown.Popover>
        )}
      </div>
      <InputDetail errorMessage={errorMessage} />
    </div>
  );
}

AddressDropdown.Popover = function Popover({
  children,
  state,
  testid,
  ...props
}: PopoverProps) {
  const { popoverProps } = usePopover(props, state);

  return (
    <Overlay>
      <div
        {...popoverProps}
        data-testid={testid}
        ref={props.popoverRef as RefObject<HTMLDivElement>}
        style={{
          ...popoverProps.style,
          ...props.style,
        }}
        className={`${styles.popover} ${props.className}`}
      >
        {children}
        <DismissButton onDismiss={state.close} />
      </div>
    </Overlay>
  );
};
